import { FunctionComponent, SyntheticEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { Div, Dropdown, Gutter, Image, OptionListOption } from '@gaiads/telia-react-component-library';
import { UserOrganizationResponse, UserRole } from '@cdaas/api-clients/dist/asset-user-openapi';

import { getOrganizationHierarchyForAdmin, getPendingApprovalChains, hasUserARequiredRole } from '../../utils';
import { RootState } from '../../store/reducers';
import {
  authSetPendingApprovals,
  authSetSelectedOrganization,
  shopClearOrderForOrganization,
  shopClearOrderOnBehalfUser,
  shopResetProduct,
} from '../../store/actions';

import AlertIcon from '../../assets/images/telia-alert-filled-icon.svg';
import './OrganizationSelector.scss';

interface OptionListOptionWithOrganization extends OptionListOption {
  organization: UserOrganizationResponse;
  hasPendingApprovals?: boolean;
}

interface OrganizationSelectorProps {
  selectedOrganization: OptionListOptionWithOrganization | undefined;
  handleOrganizationChange: (_e: SyntheticEvent, option: OptionListOption) => void;
  organizationList: OptionListOptionWithOrganization[];
}

const OrganizationSelector: FunctionComponent<OrganizationSelectorProps> = ({ selectedOrganization, handleOrganizationChange, organizationList }) => {
  return <Dropdown selectedOption={selectedOrganization} options={organizationList} onChange={handleOrganizationChange} showCustomLabel={true} />;
};

interface WrappedOrganizationSelectorProps {
  position: 'top' | 'bottom';
}

export const WrappedOrganizationSelector: FunctionComponent<WrappedOrganizationSelectorProps> = ({ position }) => {
  const dispatch = useDispatch();
  const { t: tGlobal } = useTranslation('globalComponents');

  const [organizationList, setOrganizationList] = useState<OptionListOptionWithOrganization[] | null>(null);
  const [selectedOrganization, setSelectedOrganization] = useState<OptionListOptionWithOrganization>();
  const [pendingApprovalsInOtherOrganizations, setPendingApprovalsInOtherOrganizations] = useState(false);

  const loggedIn = useSelector((state: RootState) => state.authentication.authenticated);
  const ownOrganizationId = useSelector((state: RootState) => state.authentication.user?.organization?.id);
  const selectedOrganizationRedux = useSelector((state: RootState) => state.authentication.selectedOrganization);

  const handleOrganizationChange = (_e: SyntheticEvent, option: OptionListOption): void => {
    if (option.value === selectedOrganization?.value) {
      return;
    }
    const foundOrganization = organizationList!.find(org => org.value === option.value)!;
    setSelectedOrganization(foundOrganization);
    dispatch(shopResetProduct());
    dispatch(shopClearOrderOnBehalfUser());
    dispatch(shopClearOrderForOrganization());
    dispatch(authSetPendingApprovals(0));
    dispatch(authSetSelectedOrganization(foundOrganization.organization, true));
  };

  const findInitialOrganization = (
    organizationList: UserOrganizationResponse[],
    ownOrgId: string,
    selectedOrganizationRedux: UserOrganizationResponse | null,
  ): OptionListOptionWithOrganization | undefined => {
    if (selectedOrganizationRedux) {
      return { label: selectedOrganizationRedux.name, value: selectedOrganizationRedux.id, organization: selectedOrganizationRedux };
    }

    const ownOrganization = organizationList.find(org => org.id === ownOrganizationId);
    return ownOrganization && ownOrgId ? { label: ownOrganization.name, value: ownOrganization.id, organization: ownOrganization } : undefined;
  };

  const processPendingApprovals = async (organizationOptionList: OptionListOptionWithOrganization[] | null, selectedOrganizationId?: string) => {
    if (!organizationOptionList || !selectedOrganizationId) return;

    const listClone = await Promise.all(
      organizationOptionList.map(async org => ({
        ...org,
        checked:
          (org.value !== selectedOrganizationId && ((await getPendingApprovalChains(org.organization.id, 1))?.contents ?? []).length > 0) ||
          undefined,
      })),
    );

    const pendingApprovalsFoundInOtherOrganizations = listClone.some(org => org.checked);

    setPendingApprovalsInOtherOrganizations(pendingApprovalsFoundInOtherOrganizations);
    setOrganizationList(listClone);
  };

  const fetchOrganizationList = async (ownOrgId: string) => {
    const organizationList = await getOrganizationHierarchyForAdmin();
    if (organizationList && ownOrgId && organizationList.length > 1) {
      const optionList = organizationList.map(org => ({ label: org.name, value: org.id, organization: org }));
      setOrganizationList(optionList);
      setSelectedOrganization(findInitialOrganization(organizationList, ownOrgId, selectedOrganizationRedux));
    }
  };

  useEffect(() => {
    processPendingApprovals(organizationList, selectedOrganization?.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrganization, ownOrganizationId]);

  useEffect(() => {
    if (loggedIn && !!ownOrganizationId && hasUserARequiredRole([UserRole.Admin])) {
      fetchOrganizationList(ownOrganizationId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn, ownOrganizationId]);

  useEffect(() => {
    if (selectedOrganizationRedux && selectedOrganization?.value !== selectedOrganizationRedux.id) {
      if (hasUserARequiredRole([UserRole.Approver])) {
        setPendingApprovalsInOtherOrganizations(false);
      }
      setSelectedOrganization({
        label: selectedOrganizationRedux.name,
        value: selectedOrganizationRedux.id,
        organization: selectedOrganizationRedux,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrganizationRedux]);

  if (organizationList) {
    return (
      <div className={position === 'top' ? 'organization-selector__wrapper--bigger-screen' : 'organization-selector__wrapper'}>
        <Gutter inlineBlock size="xs" />
        <Div inlineBlock className="organization-selector-override">
          <OrganizationSelector
            selectedOrganization={selectedOrganization}
            handleOrganizationChange={handleOrganizationChange}
            organizationList={organizationList}
          />
        </Div>
        {pendingApprovalsInOtherOrganizations && (
          <Div inlineBlock className={'pending-approvals-image'}>
            <Image src={AlertIcon} alt={tGlobal('imageAltTexts.altPendingApprovalsImage')}></Image>
          </Div>
        )}
      </div>
    );
  }

  return <></>;
};
